import { Facility, TargetSource, TrackedTarget } from "@app/shared";
import { useCallback, useState } from "react";
import { CheckboxInput } from "../../../shared";
import { ChartBounds, DataPoint } from "../DataChart";
import { DataViewerProps } from "../DataViewer";
import { getRadarFrame, getVideoFrame } from "../ProjectWithData";
import { TargetDetailsPopup } from "../TargetDetailsPopup";
import { LayoutChart } from "./LayoutChart";
// Inlined TargetDetailsPopup component to avoid import issues


export const LayoutViewer: React.FC<DataViewerProps> = (props) => {
    const [facility] = useState<Facility>((props.project.device as any)?.facility ?? new Facility());
    //const facilityShapes = useMemo(() => [...facility?.detectionZones ?? []] as Array<Device | ScreenLine | DetectionZone>, [facility])
    const [bounds] = useState<ChartBounds>({ minX: -40, maxX: 40, minY: 0, maxY: 150 });

    // Target filter checkboxes
    const [showRadarOnlyTargets, setShowRadarOnlyTargets] = useState<boolean>(true);
    const [showCameraOnlyTargets, setShowCameraOnlyTargets] = useState<boolean>(true);
    const [showFusedTargets, setShowFusedTargets] = useState<boolean>(true);

    // Target details popup state
    const [selectedTarget, setSelectedTarget] = useState<TrackedTarget | null>(null);
    const [popupPosition, setPopupPosition] = useState<{ x: number; y: number }>({ x: 0, y: 0 });

    const radarFrame = getRadarFrame(props.project, props.currentTimeS);
    const videoFrame = getVideoFrame(props.project, props.currentTimeS);

    // Get current frame simulation targets for chart
    const getCurrentFrameTargets = useCallback(() => {
        if (!props?.project.simTargets) return [];

        const projectFrame = props.project.projectFrameType === "video" ? videoFrame : radarFrame;
        return props?.project.simTargetsByFrame?.[projectFrame] ?? [];
    }, [props.project, props.currentTimeS, radarFrame]);

    // Filter targets based on checkbox selections
    const filterTargets = useCallback((targets: any[]) => {
        return targets.filter(target => {
            // Check if target is Radar-Only (Radar source AND no associated camera target)
            const isRadarOnly = target.source === TargetSource.Radar && (!target?.associatedCameraTarget);

            // Check if target is Camera-Only (Camera source)
            const isCameraOnly = target.source === TargetSource.Camera;

            // Check if target is Fused (has associated camera target)
            const isFused = target?.associatedCameraTarget;

            // Apply filters based on checkbox states
            if (isRadarOnly && showRadarOnlyTargets) return true;
            if (isCameraOnly && showCameraOnlyTargets) return true;
            if (isFused && showFusedTargets) return true;

            return false;
        });
    }, [showRadarOnlyTargets, showCameraOnlyTargets, showFusedTargets]);

    const allTargets = getCurrentFrameTargets();
    const filteredTargets = filterTargets(allTargets);

    const handleTargetClick = useCallback((datasetIndex: number, pointIndex: number, point: DataPoint, event?: MouseEvent) => {
        let clickedTarget = point.key as TrackedTarget;
        if (!clickedTarget)
            return;

        setSelectedTarget(clickedTarget);
        setPopupPosition({ x: window.innerWidth / 4, y: window.innerHeight / 4 });
    }, [filteredTargets]);

    return (
        <div className="w-full flex flex-col gap-3">
            {/* Target Filter Checkboxes */}
            <div className="flex flex-row justify-center place-content-center place-items-center gap-4 border-b pb-3">
                <CheckboxInput
                    label="Radar-Only"
                    onChange={(e) => setShowRadarOnlyTargets(e.target.checked)}
                    checked={showRadarOnlyTargets}
                />
                <CheckboxInput
                    label="Camera-Only"
                    onChange={(e) => setShowCameraOnlyTargets(e.target.checked)}
                    checked={showCameraOnlyTargets}
                />
                <CheckboxInput
                    label="Fused"
                    onChange={(e) => setShowFusedTargets(e.target.checked)}
                    checked={showFusedTargets}
                />
            </div>

            <div className="flex flex-row justify-center place-content-center place-items-center gap-4">
                <span className="text-sm text-gray-600">
                    Total: {allTargets.length} | Shown: {filteredTargets.length}
                </span>
            </div>
            <LayoutChart
                targets={filteredTargets}
                laneLines={(facility as any)?.laneLines ?? []}
                detectionZones={facility?.detectionZones ?? []}
                screenLines={facility?.screenLines ?? []}
                bounds={bounds}
                devicePosition={{ x: props.project.device?.positionX ?? 0, y: props.project.device?.positionY ?? 0 }}
                deviceHeading={props.project.device?.headingDegrees ?? 0}
                videoFrame={videoFrame}
                radarFrame={radarFrame}
                onTargetClick={handleTargetClick}
            />

            {/* Target Details Popup */}
            <TargetDetailsPopup
                target={selectedTarget}
                position={popupPosition}
                onClose={() => setSelectedTarget(null)}
                laneLines={facility?.laneLines ?? []}
            />
        </div>
    );
}